import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useEmailComposerContext } from '../../../../../models/Email';
import { Checkbox } from '@HoudiniUI/Checkbox';
import { Field, FieldLabel } from '@HoudiniUI/Field';
import { useGetSurveyById } from '../../../../../queries';
import { ComposeEventRegistrationSurveyFollowUpEmailViewModel } from '../../../../../viewmodels/AppViewModels';
import { grayIconFill } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { InfoIcon } from '../../../svgs/icons/InfoIcon';
import { PopoverType, TinyPopover } from '../../../TinyPopover';
import {
	FollowUpRecipientDiscriminator,
	IFollowUpRecipientDiscriminatorOption,
} from '../FollowUpRecipientDiscriminator';
import { styleSheet } from './styles';
import {
	buildAllRecipientsOption,
	buildFollowUpStatusOption,
	getKnownContactStats,
	getNextFollowUpSelection,
	getSelectionForIncludeGuests,
} from './utils';

interface IProps {
	styles?: StyleDeclarationValue[];
}

export function EventRegistrationSurveyFollowUpOptions({ styles }: IProps) {
	const { emailComposer } = useEmailComposerContext<
		Api.ISurveyFollowUpOptions,
		Api.EventSurveyReportViewModel,
		ComposeEventRegistrationSurveyFollowUpEmailViewModel
	>();

	const surveyId = emailComposer.followUpSource.survey.id;
	const { data, isPending } = useGetSurveyById<Api.IEventRegistrationSurvey>({
		surveyId,
	});

	const [includeGuests, setIncludeGuests] = React.useState<boolean>(
		Boolean(emailComposer.emailMessage.options.followUp?.surveyResponseFilter?.includeGuests)
	);
	const hasGuestContacts = (data?.stats?.guestContacts?.total || 0) > 0;

	const options =
		React.useMemo((): IFollowUpRecipientDiscriminatorOption<Api.EventRegistrationResponseStatus | null>[] => {
			if (!getKnownContactStats(data)) {
				return [];
			}

			const option = (status: Api.EventRegistrationResponseStatus) =>
				buildFollowUpStatusOption(data, status, includeGuests);
			const checkedIn = option(Api.EventRegistrationResponseStatus.CheckedIn);

			return [
				buildAllRecipientsOption(data, includeGuests),
				option(Api.EventRegistrationResponseStatus.Attending),
				...(checkedIn.count > 0 ? [checkedIn] : []),
				option(Api.EventRegistrationResponseStatus.NotAttending),
				option(Api.EventRegistrationResponseStatus.NoResponse),
			];
		}, [data, includeGuests]);

	const [selectedOptions, setSelectedOptions] = React.useState<
		IFollowUpRecipientDiscriminatorOption<Api.EventRegistrationResponseStatus | null>[]
	>([]);

	// A reopened draft carries its statuses on the filter; show them as selected once the counts load.
	const hasSeededFromFilter = React.useRef(false);
	React.useEffect(() => {
		if (hasSeededFromFilter.current || !options.length) {
			return;
		}

		hasSeededFromFilter.current = true;
		const savedStatuses = emailComposer.emailMessage.options.followUp?.surveyResponseFilter?.status ?? [];
		if (savedStatuses.length) {
			setSelectedOptions(
				savedStatuses.map(status => buildFollowUpStatusOption(data, status, includeGuests)).filter(x => !x.disabled)
			);
		}
	}, [options, data, includeGuests, emailComposer]);

	const updateFilter = (statuses: Api.EventRegistrationResponseStatus[], nextIncludeGuests: boolean) => {
		if (emailComposer.emailMessage.options.followUp) {
			emailComposer.emailMessage.options.followUp.surveyResponseFilter = {
				...(emailComposer.emailMessage.options.followUp.surveyResponseFilter || {}),

				surveyId,
				status: statuses,
				includeGuests: nextIncludeGuests,
				_type: 'EventRegistrationSurveyResponseFilterRequest',
			};
		}
	};

	const toStatuses = (selected: IFollowUpRecipientDiscriminatorOption<Api.EventRegistrationResponseStatus | null>[]) =>
		selected.map(x => x.representedObject).filter((x): x is Api.EventRegistrationResponseStatus => x !== null);

	const onIncludeGuestsChanged = (checked: boolean) => {
		setIncludeGuests(checked);
		// Option ids carry their counts, so carry the chosen statuses over to the recounted options.
		const nextSelected = getSelectionForIncludeGuests(data, selectedOptions, checked);
		setSelectedOptions(nextSelected);
		updateFilter(toStatuses(nextSelected), checked);
	};

	// Keyed on statuses rather than the selection, since "All recipients" alone can be checked with no status behind it.
	const includeGuestsDisabled = !toStatuses(selectedOptions).length && !includeGuests;

	const onSelectedOptionsChanged = (
		o: IFollowUpRecipientDiscriminatorOption<Api.EventRegistrationResponseStatus | null>[]
	) => {
		const nextSelected = getNextFollowUpSelection(selectedOptions, o, options);
		// With nothing selected the guest option is disabled, so it can't be left on out of sight.
		const nextIncludeGuests = includeGuests && toStatuses(nextSelected).length > 0;

		updateFilter(toStatuses(nextSelected), nextIncludeGuests);
		setIncludeGuests(nextIncludeGuests);
		setSelectedOptions(nextSelected);
	};

	return (
		<div className={`${css(styleSheet.container, ...styles)}`}>
			<h4 className={css(styleSheet.header)}>
				Select Recipients to Follow-Up
				<InfoPopover anchorStyles={[styleSheet.infoPopoverAnchor]}>
					{hasGuestContacts
						? 'Counts are for registrants with a contact record, plus their guests when guests are included.'
						: 'Recipient count is for known contacts not including guests.'}
				</InfoPopover>
			</h4>
			<div className={css(styleSheet.content)}>
				{isPending ? (
					<LoadingSpinner type='small' />
				) : (
					<>
						<FollowUpRecipientDiscriminator
							multiSelect={true}
							onSelectedOptionsChanged={onSelectedOptionsChanged}
							options={options}
							selectedOptions={selectedOptions}
						/>
						{hasGuestContacts || includeGuests ? (
							<div className={css(styleSheet.includeGuests)}>
								<Field orientation='horizontal' data-disabled={includeGuestsDisabled ? 'true' : undefined}>
									<Checkbox
										id='follow-up-include-guests'
										checked={includeGuests}
										disabled={includeGuestsDisabled}
										onCheckedChange={value => onIncludeGuestsChanged(value === true)}
									/>
									<FieldLabel htmlFor='follow-up-include-guests' styles={[styleSheet.includeGuestsLabel]}>
										Include guests of selected recipients
									</FieldLabel>
									<InfoPopover>
										Send an email to guests who provided an email when registering, but only guests of the registrants
										you selected above. For example, with Attending selected, guests of registrants who aren&apos;t
										attending are left out.
									</InfoPopover>
								</Field>
							</div>
						) : null}
					</>
				)}
			</div>
		</div>
	);
}

function InfoPopover({
	anchorStyles,
	children,
}: {
	anchorStyles?: StyleDeclarationValue[];
	children: React.ReactNode;
}) {
	return (
		<TinyPopover
			anchor={
				<figure className={css(baseStyleSheet.flex, baseStyleSheet.itemsCenter)}>
					<InfoIcon fillColor={grayIconFill} />
				</figure>
			}
			anchorStyles={anchorStyles}
			dismissOnOutsideAction={true}
			toggleOnHover={true}
			autoCloseOtherPopoversOnHover={true}
			type={PopoverType.background}
			placement={['right', 'top', 'bottom']}
		>
			<p className={css(styleSheet.infoPopoverContent)}>{children}</p>
		</TinyPopover>
	);
}
